import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  DollarSign, TrendingUp, GraduationCap, Wallet, Shield, AlertTriangle,
  ChevronRight, Sparkles, BarChart3, PiggyBank, Calculator, Target,
  CheckCircle2, XCircle, Lightbulb, Info, ArrowRight, Briefcase,
  Clock, Award
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SituationStrip from '../components/SituationStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } } };

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);

const ALERT_CFG = {
  critical: { icon: XCircle, color: 'text-[#C01A49]', bg: 'bg-red-500/[0.06]', border: 'border-red-500/25' },
  warning: { icon: AlertTriangle, color: 'text-[#ECC451]', bg: 'bg-amber-500/[0.06]', border: 'border-amber-500/25' },
  opportunity: { icon: Lightbulb, color: 'text-[#15BA89]', bg: 'bg-emerald-500/[0.06]', border: 'border-emerald-500/25' },
  info: { icon: Info, color: 'text-[#39E3DC]', bg: 'bg-cyan-500/[0.06]', border: 'border-cyan-500/25' },
};

export default function CollegeFinancialPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/college/financial-intelligence`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-20 text-slate-300">Unable to load financial intelligence.</div></AppLayout>;

  const { student, tuition, debt, earnings, lifestyle, aid, readiness_score, alerts, sources } = data;

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5 max-w-[1400px] mx-auto" data-testid="financial-intelligence">

        {/* HERO */}
        <motion.div variants={fadeUp}>
          <div className="relative overflow-hidden rounded-3xl border border-[rgba(57,227,220,0.28)] bg-[#061736] p-7 md:p-9"
            style={{ boxShadow: '0 0 80px -20px rgba(57,227,220,0.45), 0 40px 100px -60px rgba(57,227,220,0.5)' }}>
            <div aria-hidden className="pointer-events-none absolute -top-28 -right-20 w-[26rem] h-[26rem] rounded-full blur-3xl opacity-25"
              style={{ background: 'radial-gradient(circle, #39E3DC, transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 w-[20rem] h-[20rem] rounded-full blur-3xl opacity-15"
              style={{ background: 'radial-gradient(circle, #15BA89, transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.6), transparent)' }} />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ boxShadow: '0 0 8px rgba(21,186,137,0.6)' }} />
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#15BA89] font-bold">Financial Intelligence</p>
              </div>
              <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight"
                style={{ textShadow: '0 0 60px rgba(57,227,220,0.2)' }}>
                Your Financial Picture
              </h1>
              <p className="text-base text-slate-300/90 mt-2 max-w-2xl">
                Full financial view: what college costs, what you'll earn, and how to maximize your investment in <span className="font-semibold text-[#39E3DC]">{student.major}</span>.
              </p>
            </div>
          </div>
        </motion.div>

        {/* SITUATION STRIP (canonical context) */}
        <motion.div variants={fadeUp}><SituationStrip page="finances" /></motion.div>

        {/* READINESS + KEY METRICS */}
        <motion.div variants={fadeUp}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <MetricTile label="Financial Readiness" value={`${readiness_score}%`} icon={Shield} color="#39E3DC" glow />
            <MetricTile label="Entry Salary" value={fmt(earnings.entry_salary)} icon={Briefcase} color="#15BA89" />
            <MetricTile label="Remaining Cost" value={fmt(tuition.remaining_cost)} icon={GraduationCap} color="#f59e0b" />
            <MetricTile label="Projected Debt" value={fmt(debt.estimated_at_grad)} icon={DollarSign} color="#C01A49" />
            <MetricTile label="Monthly Payment" value={fmt(debt.monthly_payment)} icon={Clock} color="#782FE1" />
            <MetricTile label="ROI Ratio" value={`${earnings.roi_ratio}x`} icon={TrendingUp} color="#39E3DC" />
          </div>
        </motion.div>

        {/* TWO COLUMN: Lifetime Earnings + College Cost */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Lifetime Earnings */}
          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(21,186,137,0.28)] bg-[#061736] p-6 h-full"
              style={{ boxShadow: '0 0 50px -14px rgba(21,186,137,0.25)' }}>
              <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-10"
                style={{ background: 'radial-gradient(circle, #15BA89, transparent 65%)' }} />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.08] flex items-center justify-center"
                    style={{ boxShadow: '0 0 14px rgba(21,186,137,0.2)' }}>
                    <TrendingUp className="w-5 h-5 text-[#15BA89]" style={{ filter: 'drop-shadow(0 0 4px rgba(21,186,137,0.5))' }} />
                  </div>
                  <div>
                    <h2 className="text-white font-outfit font-bold text-xl">Earning Trajectory</h2>
                    <p className="text-slate-300 text-xs">{student.career_target} salary growth</p>
                  </div>
                </div>

                {/* Salary progression */}
                <div className="space-y-3">
                  <SalaryBar label="Entry Level (0-2 yrs)" value={earnings.entry_salary} max={earnings.senior} color="#15BA89" />
                  <SalaryBar label="Mid Career (5-10 yrs)" value={earnings.mid_career} max={earnings.senior} color="#39E3DC" />
                  <SalaryBar label="Senior Level (15+ yrs)" value={earnings.senior} max={earnings.senior} color="#782FE1" />
                </div>

                <div className="mt-5 rounded-xl border border-[rgba(21,186,137,0.22)] bg-emerald-500/[0.04] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-[#15BA89]/80 uppercase tracking-wider font-bold">Lifetime Earnings Premium</p>
                      <p className="text-2xl font-outfit font-black text-white mt-1" style={{ textShadow: '0 0 20px rgba(21,186,137,0.15)' }}>
                        {fmt(earnings.lifetime_premium)}
                      </p>
                      <p className="text-[10px] text-slate-300 mt-0.5">vs. no degree over 40-year career</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-outfit font-black text-[#15BA89]" style={{ textShadow: '0 0 16px rgba(21,186,137,0.3)' }}>
                        {earnings.roi_ratio}x
                      </p>
                      <p className="text-[10px] text-slate-300">Return on Investment</p>
                    </div>
                  </div>
                </div>

                <Link to="/app/future-outlook"
                  className="mt-4 flex items-center gap-1.5 text-xs text-[#39E3DC] hover:text-[#39E3DC] transition-colors">
                  Full Earnings Projection <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* College Cost Intelligence */}
          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(236,196,81,0.28)] bg-[#061736] p-6 h-full"
              style={{ boxShadow: '0 0 50px -14px rgba(236,196,81,0.2)' }}>
              <div aria-hidden className="pointer-events-none absolute -top-10 -left-10 w-32 h-32 rounded-full blur-3xl opacity-10"
                style={{ background: 'radial-gradient(circle, #ECC451, transparent 65%)' }} />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] flex items-center justify-center"
                    style={{ boxShadow: '0 0 14px rgba(236,196,81,0.2)' }}>
                    <GraduationCap className="w-5 h-5 text-[#ECC451]" style={{ filter: 'drop-shadow(0 0 4px rgba(236,196,81,0.5))' }} />
                  </div>
                  <div>
                    <h2 className="text-white font-outfit font-bold text-xl">College Cost</h2>
                    <p className="text-slate-300 text-xs">{student.institution} | {student.semesters_remaining} semesters left</p>
                  </div>
                </div>

                {/* Cost breakdown */}
                <div className="space-y-2">
                  {Object.entries(tuition.breakdown).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between rounded-lg border border-white/[0.04] bg-white/[0.02] px-3 py-2">
                      <span className="text-xs text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-white font-semibold">{fmt(val)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-lg border border-[rgba(236,196,81,0.28)] bg-amber-500/[0.06] px-3 py-2 mt-1">
                    <span className="text-xs text-[#ECC451] font-bold">Annual Cost of Attendance</span>
                    <span className="text-sm text-white font-bold">{fmt(tuition.annual_coa)}</span>
                  </div>
                </div>

                {/* Remaining cost */}
                <div className="mt-4 rounded-xl border border-[rgba(236,196,81,0.22)] bg-amber-500/[0.04] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-[#ECC451]/80 uppercase tracking-wider font-bold">Remaining to Graduate</p>
                      <p className="text-2xl font-outfit font-black text-white mt-1">{fmt(tuition.remaining_cost)}</p>
                    </div>
                    {aid.estimated_grant_aid > 0 && (
                      <div className="text-right">
                        <p className="text-[10px] text-[#15BA89]/80 uppercase tracking-wider font-bold">Avg. Grant Aid</p>
                        <p className="text-lg font-bold text-[#15BA89]">-{fmt(aid.estimated_grant_aid)}/yr</p>
                      </div>
                    )}
                  </div>
                </div>

                <Link to="/app/tuition-calculator"
                  className="mt-4 flex items-center gap-1.5 text-xs text-[#39E3DC] hover:text-[#39E3DC] transition-colors">
                  Detailed Cost Calculator <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* DEBT + LIFESTYLE ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Debt Analysis */}
          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(192,26,73,0.28)] bg-[#061736] p-5 h-full"
              style={{ boxShadow: '0 0 40px -14px rgba(192,26,73,0.2)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg border border-red-400/25 bg-red-500/[0.08] flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-[#C01A49]" />
                </div>
                <h2 className="text-white font-outfit font-bold text-base">Debt Projection</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-300">Est. Debt at Graduation</span>
                  <span className="text-sm text-white font-bold">{fmt(debt.estimated_at_grad)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-300">Monthly Payment</span>
                  <span className="text-sm text-white font-bold">{fmt(debt.monthly_payment)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-300">Repayment Period</span>
                  <span className="text-sm text-white font-bold">{debt.repayment_years} years</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-300">Interest Rate</span>
                  <span className="text-sm text-white font-bold">{debt.interest_rate}</span>
                </div>
                <div className="h-px bg-white/[0.06]" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-300">Debt-to-Income</span>
                  <span className={`text-sm font-bold ${debt.debt_to_income > 60 ? 'text-[#C01A49]' : debt.debt_to_income > 40 ? 'text-[#ECC451]' : 'text-[#15BA89]'}`}>
                    {debt.debt_to_income}%
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Lifestyle Readiness */}
          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5 h-full"
              style={{ boxShadow: '0 0 40px -14px rgba(57,227,220,0.2)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg border border-[rgba(57,227,220,0.28)] bg-cyan-500/[0.08] flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-[#39E3DC]" />
                </div>
                <h2 className="text-white font-outfit font-bold text-base">Lifestyle & Income</h2>
              </div>
              {lifestyle.started ? (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[rgba(89,126,190,0.22)] bg-cyan-500/[0.04] p-4 text-center">
                    <p className="text-[10px] text-[#39E3DC]/80 uppercase tracking-wider mb-1">Required Annual Income</p>
                    <p className="text-2xl font-outfit font-black text-white" style={{ textShadow: '0 0 20px rgba(57,227,220,0.15)' }}>
                      {fmt(lifestyle.required_income)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                      <p className="text-[10px] text-slate-300 uppercase">Monthly</p>
                      <p className="text-sm font-bold text-white">{fmt(lifestyle.monthly_need)}</p>
                    </div>
                    <div className="flex-1 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                      <p className="text-[10px] text-slate-300 uppercase">Confidence</p>
                      <p className="text-sm font-bold text-[#39E3DC] capitalize">{lifestyle.confidence || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-[#39E3DC]/50" />
                  <p className="text-sm text-slate-300 mb-3">Design your ideal lifestyle to unlock your income target.</p>
                  <Link to="/app/calculator"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
                    style={{ background: 'linear-gradient(90deg, #352CA0 0%, #782FE1 50%, #2E86E2 100%)', boxShadow: '0 0 12px rgba(120,47,225,0.55), 0 0 30px rgba(46,134,226,0.25)' }}>
                    <Calculator className="w-4 h-4" /> Design My Life
                  </Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Financial Aid */}
          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(120,47,225,0.35)] bg-[#061736] p-5 h-full"
              style={{ boxShadow: '0 0 40px -14px rgba(120,47,225,0.2)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg border border-violet-400/25 bg-violet-500/[0.08] flex items-center justify-center">
                  <Award className="w-4 h-4 text-[#782FE1]" />
                </div>
                <h2 className="text-white font-outfit font-bold text-base">Financial Aid</h2>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">FAFSA Status</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                    aid.fafsa_filed
                      ? 'border-emerald-400/30 bg-emerald-500/10 text-[#15BA89]'
                      : 'border-amber-400/30 bg-amber-500/10 text-[#ECC451]'
                  }`}>
                    {aid.fafsa_filed ? 'Filed' : 'Not Filed'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">Avg. Grant Aid</span>
                  <span className="text-sm text-[#15BA89] font-bold">{fmt(aid.estimated_grant_aid)}/yr</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">Net Annual Cost</span>
                  <span className="text-sm text-white font-bold">{fmt(aid.net_annual_cost)}</span>
                </div>
                <div className="h-px bg-white/[0.06]" />
                <Link to="/app/funding"
                  className="flex items-center gap-1.5 text-xs text-[#782FE1] hover:text-violet-200 transition-colors">
                  Explore Grants & Aid <ChevronRight className="w-3 h-3" />
                </Link>
                <Link to="/app/scholarships"
                  className="flex items-center gap-1.5 text-xs text-[#782FE1] hover:text-violet-200 transition-colors">
                  Scholarship Matching <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ALERTS */}
        {alerts?.length > 0 && (
          <motion.div variants={fadeUp}>
            <div className="space-y-2" data-testid="financial-alerts">
              {alerts.map((alert, i) => {
                const cfg = ALERT_CFG[alert.type] || ALERT_CFG.info;
                const AlertIcon = cfg.icon;
                return (
                  <div key={i} className={`rounded-xl border ${cfg.border} ${cfg.bg} p-3.5`}>
                    <div className="flex items-start gap-3">
                      <AlertIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cfg.color}`} />
                      <div className="min-w-0">
                        <p className={`text-xs font-bold ${cfg.color}`}>{alert.title}</p>
                        <p className="text-slate-300 text-[11px] mt-0.5 leading-relaxed">{alert.message}</p>
                      </div>
                      {alert.link && (
                        <Link to={alert.link} className="flex-shrink-0 text-xs text-[#39E3DC] hover:text-[#39E3DC]">
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* QUICK LINKS */}
        <motion.div variants={fadeUp}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickLink to="/app/calculator" icon={Calculator} label="Design My Life" desc="Set your lifestyle goals" />
            <QuickLink to="/app/future-outlook" icon={TrendingUp} label="Earnings Projection" desc="Full career earnings model" />
            <QuickLink to="/app/tuition-calculator" icon={GraduationCap} label="College Costs" desc="Detailed cost breakdown" />
            <QuickLink to="/app/funding" icon={PiggyBank} label="Grants & Aid" desc="Financial aid options" />
          </div>
        </motion.div>

        {/* Sources */}
        <motion.div variants={fadeUp}>
          <div className="rounded-2xl border border-white/[0.06] bg-[#030C1D] p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Data Sources</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {sources.map((s, i) => (
                <span key={i} className="text-[11px] text-slate-300 px-2.5 py-1 rounded-full border border-white/[0.06] bg-white/[0.02]">{s}</span>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
}

function MetricTile({ label, value, icon: Icon, color, glow }) {
  return (
    <div className={`rounded-xl border p-4 text-center transition-all duration-300 hover:scale-[1.02] ${
      glow ? 'border-cyan-400/30 bg-[#061736]' : 'border-white/[0.06] bg-[#030C1D]'
    }`}
      style={glow ? { boxShadow: `0 0 30px -8px ${color}55` } : { boxShadow: `0 0 16px -6px ${color}33` }}>
      <Icon className="w-5 h-5 mx-auto mb-2" style={{ color, filter: `drop-shadow(0 0 4px ${color}55)` }} />
      <p className="text-lg font-outfit font-black text-white" style={{ textShadow: `0 0 12px ${color}22` }}>{value}</p>
      <p className="text-[10px] text-slate-300 mt-0.5">{label}</p>
    </div>
  );
}

function SalaryBar({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-300">{label}</span>
        <span className="text-xs font-bold text-white">{fmt(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
        <motion.div className="h-full rounded-full"
          initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          style={{ background: `linear-gradient(90deg, ${color}, ${color}88)`, boxShadow: `0 0 10px ${color}55` }} />
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to}
      className="block rounded-xl border border-[rgba(89,126,190,0.18)] bg-[#030C1D] p-4 hover:border-[rgba(57,227,220,0.28)] hover:[#061736] transition-all duration-300 group"
      style={{ boxShadow: '0 0 16px -8px rgba(57,227,220,0.12)' }}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-[#39E3DC] group-hover:text-[#39E3DC] transition-colors" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold group-hover:text-[#39E3DC] transition-colors">{label}</p>
          <p className="text-[10px] text-slate-300">{desc}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#39E3DC] transition-colors flex-shrink-0" />
      </div>
    </Link>
  );
}
